import { useEffect, useState } from "react";
import { PageHeader } from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { HeartHandshake } from "lucide-react";
import { SEO } from "@/components/SEO";

const TIERS = [500, 1000, 2500, 5000];

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

export default function Donate() {
  const [amount, setAmount] = useState(1000);
  const [customAmount, setCustomAmount] = useState("");
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    loadRazorpayScript();
  }, []);

  const finalAmount = customAmount !== "" ? Number(customAmount) : amount;

  const handleDonate = async (e) => {
    e.preventDefault();
    if (!finalAmount || finalAmount < 1) {
      toast.error("Please enter a valid donation amount.");
      return;
    }
    if (!form.name || !form.email) {
      toast.error("Please fill your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      const { data: order } = await api.post("/donations/create-order", {
        amount: finalAmount,
        donor_name: form.name,
        donor_email: form.email,
        donor_phone: form.phone,
      });

      const ok = await loadRazorpayScript();
      if (!ok || !window.Razorpay) {
        toast.error("Unable to load payment gateway. Please try again.");
        setSubmitting(false);
        return;
      }

      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        order_id: order.order_id,
        name: "PhysioAbhi Healthcare Foundation",
        description: "Donation towards physiotherapy access",
        prefill: { name: form.name, email: form.email, contact: form.phone },
        theme: { color: "#D96C4A" },
        handler: async (response) => {
          try {
            await api.post("/donations/verify", {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            setSuccess(true);
            toast.success("Thank you for your generous donation!");
          } catch {
            toast.error("Payment verification failed. Please contact us.");
          } finally {
            setSubmitting(false);
          }
        },
        modal: { ondismiss: () => setSubmitting(false) },
      });
      rzp.open();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div data-testid="donate-success" className="min-h-screen flex items-center justify-center px-5 pt-24 text-center">
        <div>
          <HeartHandshake className="h-12 w-12 text-accent mx-auto mb-6" strokeWidth={1.5} />
          <h1 className="font-heading text-3xl md:text-4xl mb-4">Thank you for your generosity.</h1>
          <p className="text-muted-foreground max-w-md mx-auto">Your donation of ₹{finalAmount} helps us bring physiotherapy to those who need it most.</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="donate-page">
      <SEO
        title="Donate"
        description="Donate to PhysioAbhi Healthcare Foundation and fund physiotherapy sessions for elderly people, special needs children, and underserved athletes. Tax-exempt under Section 80G."
        path="/donate"
      />
      <PageHeader
        eyebrow="Donate"
        title="Fund a session. Change a life."
        subtitle="100% Section 8 nonprofit. Donations are eligible for tax exemption under Section 80G."
      />
      <section className="px-5 md:px-10 py-16">
        <Reveal className="max-w-xl mx-auto">
          <form onSubmit={handleDonate} className="space-y-8">
            <div>
              <Label className="mb-3 block text-sm font-medium">Choose an amount (₹)</Label>
              <div className="grid grid-cols-4 gap-3">
                {TIERS.map((t) => (
                  <button
                    key={t}
                    type="button"
                    data-testid={`donate-tier-${t}`}
                    onClick={() => { setAmount(t); setCustomAmount(""); }}
                    className={`py-3 border text-sm font-medium transition-colors ${amount === t && !customAmount ? "border-accent bg-accent text-accent-foreground" : "border-border hover:border-accent"}`}
                  >
                    ₹{t}
                  </button>
                ))}
              </div>
              <Input
                data-testid="donate-custom-amount-input"
                type="number"
                placeholder="Or enter custom amount"
                value={customAmount}
                onChange={(e) => setCustomAmount(e.target.value)}
                className="rounded-none mt-3"
                min="1"
              />
            </div>

            <div className="space-y-4">
              <div>
                <Label htmlFor="donor-name" className="text-sm font-medium">Full Name</Label>
                <Input id="donor-name" data-testid="donate-name-input" required className="rounded-none mt-1.5" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="donor-email" className="text-sm font-medium">Email</Label>
                <Input id="donor-email" type="email" data-testid="donate-email-input" required className="rounded-none mt-1.5" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="donor-phone" className="text-sm font-medium">Phone (optional)</Label>
                <Input id="donor-phone" data-testid="donate-phone-input" className="rounded-none mt-1.5" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
            </div>

            <Button type="submit" disabled={submitting} data-testid="donate-submit-button" size="lg" className="w-full rounded-none bg-accent text-accent-foreground hover:opacity-90 h-13">
              {submitting ? "Processing…" : `Donate ₹${finalAmount || 0} Now`}
            </Button>
          </form>
        </Reveal>
      </section>
    </div>
  );
}
